import { useState, type ComponentProps, type ComponentType } from "react";
import type { Recipe } from "../Recipes-renamed/types";
import { UsersIcon, SparklesIcon, FireIcon, ClockIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { buttonVariants } from "@heroui/react";
import { Link } from "react-router";
import { ThermometerIcon } from "@/components/ThermometerIcon";
import { formatMinutes } from "@/lib/formatDuration";
import { CookModeToggle } from "@/components/CookModeToggle";
import { useWakeLock } from "@/hooks/useWakeLock";

interface RecipeDetailsProps {
    readonly recipe: Recipe;
}

interface Stat {
    readonly label: string;
    readonly value: string;
    readonly Icon: ComponentType<ComponentProps<"svg">>;
}

interface BannerStats {
    /** The anchors — what it makes and how long it takes. Rendered large. */
    readonly primary: Stat[];
    /** Supporting cooking detail. Rendered small and inline. */
    readonly supporting: Stat[];
}

const dateFormatter = new Intl.DateTimeFormat(undefined, {
    month: "long",
    year: "numeric"
});

const buildStats = (recipe: Recipe): BannerStats => {
    const primary: Stat[] = [];
    const supporting: Stat[] = [];

    // Primary anchors: yield/servings and total time.
    if (recipe.servings) {
        primary.push({
            label: recipe.isYield ? "Yield" : "Servings",
            value: recipe.servings,
            Icon: UsersIcon
        });
    }
    if (recipe.totalTime != null) {
        primary.push({ label: "Total", value: formatMinutes(recipe.totalTime), Icon: ClockIcon });
    }

    // Supporting detail: the time breakdown (components of Total) and the oven spec.
    if (recipe.prepTime != null) {
        supporting.push({ label: "prep", value: formatMinutes(recipe.prepTime), Icon: SparklesIcon });
    }
    if (recipe.cookTime != null) {
        supporting.push({ label: "cook", value: formatMinutes(recipe.cookTime), Icon: FireIcon });
    }
    // Numeric oven temps get a "°F" so they read as a spec; "Broil" stands alone.
    if (recipe.temperature) {
        const isNumeric = /^\d+$/.test(recipe.temperature);
        supporting.push({
            label: "oven",
            value: isNumeric ? `${recipe.temperature}°F` : recipe.temperature,
            Icon: ThermometerIcon
        });
    }

    return { primary, supporting };
};

export const RecipeDetails = ({ recipe }: RecipeDetailsProps) => {
    const { primary, supporting } = buildStats(recipe);
    const hasStats = primary.length > 0 || supporting.length > 0;
    const addedOn = recipe.createdAt ? dateFormatter.format(new Date(recipe.createdAt)) : null;

    // Cook mode lives here, not in the toggle, because the banner renders the
    // toggle in two responsive slots (mobile strip + desktop column) that are
    // both always mounted. One shared state + one wake lock keeps the pills in
    // sync on resize and avoids holding two locks at once.
    const [cookMode, setCookMode] = useState(false);
    const { supported: cookModeSupported } = useWakeLock(cookMode);
    const cookModeToggle = cookModeSupported ? (
        <CookModeToggle isSelected={cookMode} onChange={setCookMode} />
    ) : null;

    const addedLabel = addedOn && (
        <p className="font-display text-[10px] uppercase tracking-[0.18em] text-default-700">
            <span className="text-default-600">Added</span> <span className="text-default-foreground">{addedOn}</span>
        </p>
    );

    const editLink = (
        <Link
            className={`${buttonVariants({ variant: "ghost", size: "sm" })} text-secondary-700 hover:bg-secondary-200/60 hover:text-secondary-800`}
            to={`/recipes/edit/${recipe.id}`}
        >
            <PencilSquareIcon className="size-4" />
            Edit recipe
        </Link>
    );

    return (
        <section
            aria-label="Recipe details"
            className="relative w-full rounded-2xl border border-secondary-200/60 bg-[#d2dde4]/70 px-6 pt-7 pb-6 sm:py-7"
        >
            {/* Top ornamental mark */}
            <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 rounded-[50px] bg-[var(--bg)] px-3 leading-none">
                <span aria-hidden className="font-display text-accent-600/80 text-sm">
                    ✦
                </span>
            </div>

            {/* Mobile-only meta strip — added date left, edit affordance right */}
            <div className="mb-5 flex items-center sm:hidden">
                {addedLabel}
                <span className="ml-auto flex items-center gap-2">
                    {cookModeToggle}
                    {editLink}
                </span>
            </div>

            {/* On desktop the meta flanks the stat cluster, filling the side space and
                collapsing the vertical band a top strip would otherwise add. */}
            <div className="mx-auto grid max-w-5xl grid-cols-1 items-center gap-y-4 sm:grid-cols-[1fr_auto_1fr] sm:gap-6">
                <div className="hidden sm:block">{addedLabel}</div>

                {hasStats ? (
                    <div className="flex flex-col items-center gap-4">
                        {/* Primary row — the large anchors, stacked on mobile, side by side on sm+ */}
                        {primary.length > 0 && (
                            <div className="flex flex-row items-stretch justify-center gap-0">
                                {primary.map((stat, index) => (
                                    <div
                                        key={stat.label}
                                        className={[
                                            "flex flex-col items-center text-center px-8 sm:px-10",
                                            index > 0 ? "border-l border-secondary-300/50" : ""
                                        ].join(" ")}
                                    >
                                        <stat.Icon aria-hidden className="mb-2 size-5 text-accent-600" />
                                        <span className="font-serif text-3xl font-medium leading-none text-default-foreground sm:text-4xl">
                                            {stat.value}
                                        </span>
                                        <span className="mt-2 font-display text-[10px] font-semibold uppercase tracking-[0.18em] text-accent-700 sm:text-xs">
                                            {stat.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Supporting row — small inline detail that wraps cleanly */}
                        {supporting.length > 0 && (
                            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2.5">
                                {supporting.map((stat) => (
                                    <span key={stat.label} className="inline-flex items-baseline gap-1.5">
                                        <stat.Icon aria-hidden className="size-4 self-center text-accent-600/80" />
                                        <span className="font-serif text-base text-default-foreground">
                                            {stat.value}
                                        </span>
                                        <span className="font-display text-[10px] uppercase tracking-[0.16em] text-accent-700/90">
                                            {stat.label}
                                        </span>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <p className="text-center text-sm text-default-700">No timing or serving info recorded yet.</p>
                )}

                <div className="hidden flex-col items-end gap-2 sm:flex">
                    {cookModeToggle}
                    {editLink}
                </div>
            </div>
        </section>
    );
};
